// helpTopics.js
// The registry behind <HelpTip> (#171).
//
// One entry per object on screen that carries a number a reader can misread.
// Each entry names translation keys; it never holds prose. That is the whole
// point of the file: the help text for an object and any caveat rendered beside
// that object have to be the SAME string, so a claim cannot be corrected in one
// place and left standing in the other.
//
// Three rules hold across every entry, and they are the reason this is a
// registry rather than a prop on each call site:
//
//   1. A TOPIC NEVER RESTATES ITS LABEL. `labelKey` exists for the icon's
//      aria-label — so a screen reader says which object the help belongs to —
//      and is deliberately NOT rendered inside the tooltip. "Drift — shows
//      drift" costs a tap and returns nothing, and it teaches the reader that
//      the help is not worth opening. Every body answers: what is measured, in
//      what units, from what source, over what window, and what it is not.
//
//   2. ONE CLAIM, ONE STRING. Several `bodyKeys` below point at strings that
//      are also rendered inline somewhere — `helpNdviClaim` under the corridor
//      segment layer and in the "Connect your GIS" panel, `basemapVintage`
//      under the satellite snapshot, `watchdogScopeNote` at the foot of the
//      watchdog. That is the intended shape. Before adding a new string to
//      translations.js, check whether the claim already exists and add its key
//      here instead.
//
//      This is what resolved the `ogcCaveat` / `segmentLayerCaveat` pair, which
//      were two copies of one claim kept in step by a comment and nothing else.
//      Both are gone: `helpNdviClaim` is the claim, and `ogcCaveatGis` is the
//      one sentence that was genuinely only true of a GIS export.
//
//   3. BELOW SUPERUSER THE FIGURES ARE GENERATED, AND THE HELP SAYS SO FIRST.
//      `HELP_SIMULATED_KEY` is prepended, not appended: a tooltip explaining
//      what a drift value means, shown over a fabricated number, converts a
//      deliberate placeholder into an apparent measurement. It leads.
//
// WHY MOST TOPICS HAVE TWO BODY KEYS. The pattern is `helpX` — what the number
// is, where it comes from, over what window — then `helpXNot` — what it is not.
// They are separate keys so <HelpTip> renders them as two short paragraphs with
// a gap between them, rather than one wall of text in which the correction is
// the fourth clause of the third sentence. The audience for these includes
// people reading in a second language, and a paragraph they abandon half way
// through has told them the measurement and withheld the caveat, which is the
// worse half to lose.
//
// helpTopics.test.js asserts that every key named here resolves in both
// languages, so a topic added without its Montenegrin fails the build rather
// than rendering a raw key at a customer.

// Prepended to every body when the panel is showing generated data.
export const HELP_SIMULATED_KEY = "helpSimulatedLead";

// aria-label for the help control itself: "Help: {label}".
export const HELP_ARIA_KEY = "helpAria";

export const HELP_TOPICS = {
  // The fires chip's count, which is "n+" whenever the detections reach the
  // edge of the polled box and "?" whenever nothing answered (#100).
  // `fireCountBounded` is appended by App.js only in the clipped case — it is a
  // statement about the count on screen, not about the feature.
  firesCount: {
    labelKey: "catFires",
    bodyKeys: ["helpFiresCount", "helpFiresCountNot", "helpUnknownValue"],
  },

  // The watchdog. Its threshold is per device and estimated from that device's
  // own uplinks, which is also why the corridor report's uptime can exceed
  // 100% for a burst-reporting device (#155).
  watchdogCoverage: {
    labelKey: "watchdog",
    bodyKeys: ["helpWatchdogCoverage", "helpWatchdogCoverageNot"],
  },

  // The one-number corridor reading in the map banner: a mean over the whole
  // ~180 km² box (#103). The shared claim leads, because "what is NDVI at all"
  // has to be answered before "what is this particular average".
  corridorNdvi: {
    // Not `catSegmentNdvi`: that is the per-segment layer, and this topic is
    // the one-number banner over the whole box. A screen reader announcing
    // "Help: Corridor segments" beside the corridor-wide average would name
    // the wrong object, which is the confusion the two topics exist to keep
    // apart.
    labelKey: "helpLabelCorridorNdvi",
    bodyKeys: ["helpNdviClaim", "helpCorridorNdvi", "helpCorridorNdviNot"],
  },

  // The per-segment layer and its chip — spans of a few hundred metres between
  // consecutive towers, not the box above (#146). Same claim, same key.
  segmentNdvi: {
    labelKey: "segmentLayerTitle",
    bodyKeys: ["helpNdviClaim", "helpSegmentNdvi", "helpUnknownValue"],
  },

  // Peak and current drift on the tilt chart.
  driftValue: {
    labelKey: "currentDrift",
    bodyKeys: ["helpDriftValue", "helpDriftValueNot"],
  },

  // The three baseline figures beside them (#45).
  driftBaseline: {
    labelKey: "helpLabelBaseline",
    bodyKeys: ["helpDriftBaseline", "helpDriftBaselineNot"],
  },

  // The anomalies list: two detectors, one of which is deliberately silent
  // (#149, #152).
  mlAnomaly: {
    labelKey: "detectedAnomalies",
    bodyKeys: ["helpMlAnomaly", "helpMlAnomalyNot"],
  },

  // The per-detection score inside an expanded anomaly card.
  anomalyConfidence: {
    labelKey: "confidence",
    bodyKeys: ["helpAnomalyConfidence", "helpAnomalyConfidenceNot"],
  },

  // The temperature figure on the degradation trend, and the provenance note
  // under it (#158).
  temperatureStats: {
    labelKey: "dtTempSource",
    bodyKeys: ["helpTemperatureStats", "helpTemperatureStatsNot"],
  },

  // The per-node Sentinel-2 view, and the age of the basemap under it (#82).
  satelliteSnapshot: {
    labelKey: "satSnapshotTitle",
    bodyKeys: [
      "helpSatelliteSnapshot",
      "helpSatelliteSnapshotNot",
      "basemapVintage",
    ],
  },

  // The status dot, wherever it is drawn. The second paragraph is the one this
  // topic exists for: grey is "we cannot tell" and is never an all-clear
  // (#33, #66).
  deviceStatus: {
    labelKey: "helpLabelDeviceStatus",
    bodyKeys: ["helpDeviceStatus", "helpStatusUnknown"],
  },
};

export const HELP_TOPIC_IDS = Object.keys(HELP_TOPICS);

// Every translation key the help system can render, for the parity test and for
// anyone auditing which strings are load-bearing. Sorted and de-duplicated —
// several topics share a key on purpose.
export function allHelpKeys() {
  const keys = new Set([HELP_SIMULATED_KEY, HELP_ARIA_KEY]);
  for (const topic of Object.values(HELP_TOPICS)) {
    keys.add(topic.labelKey);
    topic.bodyKeys.forEach((k) => keys.add(k));
  }
  return [...keys].sort();
}

// The keys <HelpTip> renders, in order. `extraKeys` is for a claim that is only
// true of what is currently on screen — the clipped fire count is the one case
// — and is appended rather than folded into the registry so the registry stays
// a static description of the feature.
export function helpBodyKeys(topicId, { simulated = false, extraKeys = [] } = {}) {
  const topic = HELP_TOPICS[topicId];
  if (!topic) return [];
  return [
    ...(simulated ? [HELP_SIMULATED_KEY] : []),
    ...topic.bodyKeys,
    ...extraKeys,
  ];
}
